import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import TopNav from "../components/TopNav";
import RecentProjects from "../components/RecentProjects";
import BottomToolbar from "../components/BottomToolbar";
import NewProjectDialog from "../components/NewProjectDialog";
import UploadPlansDialog from "../components/UploadPlansDialog";
import AllProjectsDialog from "../components/AllProjectsDialog";
import EditProjectDialog from "../components/EditProjectDialog";
import SaveToWorkgroupDialog from "../components/SaveToWorkgroupDialog";
import {
    listProjects,
    createProject,
    deleteProject,
    updateProject,
    getUploadCounts,
} from "../lib/api";
import { useAuth } from "../lib/auth";

const RECENT_LIMIT = 4;

export default function Dashboard() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [newProjectOpen, setNewProjectOpen] = useState(false);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [allProjectsOpen, setAllProjectsOpen] = useState(false);
    const [activeProjectId, setActiveProjectId] = useState(null);
    const [editingProject, setEditingProject] = useState(null);
    const [uploadCounts, setUploadCounts] = useState({});
    // iter-49: "Share to workgroup" flow — opened from the per-project
    // Users icon on the Recent strip or the All-projects modal.
    const [shareProject, setShareProject] = useState(null);

    const fetchProjects = async () => {
        setProjectsLoading(true);
        try {
            const data = await listProjects();
            setProjects(data);
        } catch (e) {
            toast.error("Couldn't load projects");
        } finally {
            setProjectsLoading(false);
        }
    };

    const fetchUploadCounts = async () => {
        try {
            const data = await getUploadCounts();
            setUploadCounts(data || {});
        } catch (e) {
            // Non-fatal: the badge count just won't appear on cards.
            // eslint-disable-next-line no-console
            console.warn("Upload counts fetch failed:", e);
        }
    };

    useEffect(() => {
        fetchProjects();
        fetchUploadCounts();
    }, []);

    const handleCreateProject = async ({ name, location, job_id }) => {
        try {
            const created = await createProject({ name, location, job_id });
            setProjects((prev) => [created, ...prev]);
            setActiveProjectId(created.id);
            toast.success(`Project "${created.name}" created — upload its plans`);
            // Chain: open upload dialog right after creation
            setTimeout(() => setUploadOpen(true), 150);
        } catch (e) {
            toast.error("Failed to create project");
            throw e;
        }
    };

    const handleDeleteProject = async (project) => {
        try {
            await deleteProject(project.id);
            setProjects((prev) => prev.filter((p) => p.id !== project.id));
            toast.success(`Deleted "${project.name}"`);
        } catch (e) {
            toast.error("Couldn't delete project");
        }
    };

    const handleResetProject = async (project) => {
        const ok = window.confirm(
            `Reset "${project.name}"?\n\n` +
            "• All AI takeoff drawings on this project will be DELETED.\n" +
            "• Uploads, page splits, analyses, and estimates are preserved."
        );
        if (!ok) return;
        try {
            const { resetProject } = await import("../lib/api");
            const res = await resetProject(project.id);
            toast.success(
                `Reset "${project.name}" — cleared ${res.takeoff_items_deleted || 0} takeoff shape${res.takeoff_items_deleted === 1 ? "" : "s"}`,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Couldn't reset project"
            );
        }
    };

    const handleUpdateProject = async (id, patch) => {
        try {
            const updated = await updateProject(id, patch);
            setProjects((prev) => {
                const next = prev.map((p) => (p.id === id ? updated : p));
                // Re-sort by updated_at desc to reflect the new update time
                return [...next].sort((a, b) =>
                    (b.updated_at || "").localeCompare(a.updated_at || ""),
                );
            });
            toast.success(`Updated "${updated.name}"`);
            return updated;
        } catch (e) {
            toast.error("Couldn't update project");
            throw e;
        }
    };

    const handleUploadClose = (nextOpen) => {
        setUploadOpen(nextOpen);
        if (!nextOpen) setActiveProjectId(null);
    };

    const heroContent = {
        eyebrow: "Takeoff · Estimate · Deliver",
        title: (
            <>
                Welcome back,{" "}
                <span className="text-[#00A3FF]">
                    {(user?.full_name || "").split(" ")[0] ||
                        user?.email?.split("@")[0] ||
                        "builder"}
                </span>
            </>
        ),
        subtitle: null,
    };

    const recentProjects = projects.slice(0, RECENT_LIMIT);

    return (
        <div className="h-[100dvh] overflow-hidden blueprint-bg text-slate-100 flex flex-col">
            <TopNav active="projects" />

            <main
                className="flex-1 min-h-0 max-w-5xl w-full mx-auto px-4 md:px-8 pt-4 md:pt-8 pb-36 md:pb-32 flex flex-col"
                data-testid="dashboard-main"
            >
                <div className="flex flex-col items-center justify-center text-center shrink-0 mb-4 md:mb-8">
                    <div className="text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold text-slate-500 mb-2 md:mb-3">
                        {heroContent.eyebrow}
                    </div>
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-3 md:mb-5">
                        {heroContent.title}
                    </h1>
                    {heroContent.subtitle && (
                        <p className="text-slate-400 text-sm md:text-base max-w-xl mb-4">
                            {heroContent.subtitle}
                        </p>
                    )}
                    <button
                        onClick={() => setNewProjectOpen(true)}
                        data-testid="btn-new-project"
                        className="btn-glow inline-flex items-center gap-2 bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold uppercase tracking-wider px-6 md:px-8 py-3 md:py-4 rounded-md text-sm md:text-base"
                    >
                        <Plus className="w-5 h-5" />
                        New Project
                    </button>
                </div>

                <div className="flex-1 min-h-0 flex flex-col">
                    <RecentProjects
                        projects={recentProjects}
                        loading={projectsLoading}
                        total={projects.length}
                        uploadCounts={uploadCounts}
                        onSelect={(p) => navigate(`/workspace/${p.id}`)}
                        onViewAll={() => navigate("/projects")}
                        onNew={() => setNewProjectOpen(true)}
                        onEdit={(p) => setEditingProject(p)}
                        onReset={handleResetProject}
                        onDelete={handleDeleteProject}
                        onShareToWorkgroup={(p) => setShareProject(p)}
                    />
                </div>
            </main>

            <BottomToolbar
                onProjects={() => navigate("/projects")}
                onBuildTemplate={() => navigate("/templates")}
                onGenerateReport={undefined}
                onEmail={() => navigate("/email")}
                onContacts={() => navigate("/contacts")}
                onLeads={() => navigate("/leads")}
                onPlugins={() => navigate("/plugins")}
            />

            <NewProjectDialog
                open={newProjectOpen}
                onOpenChange={setNewProjectOpen}
                onCreate={handleCreateProject}
            />
            <UploadPlansDialog
                open={uploadOpen}
                onOpenChange={handleUploadClose}
                projectId={activeProjectId}
                onUploaded={() => {
                    fetchUploadCounts();
                }}
                onDeleted={() => {
                    fetchUploadCounts();
                }}
                onCreated={({ projectId }) => {
                    const id = projectId || activeProjectId;
                    fetchUploadCounts();
                    if (id) navigate(`/workspace/${id}`);
                }}
            />
            <AllProjectsDialog
                open={allProjectsOpen}
                onOpenChange={setAllProjectsOpen}
                projects={projects}
                uploadCounts={uploadCounts}
                onDelete={handleDeleteProject}
                onReset={handleResetProject}
                onEdit={(p) => setEditingProject(p)}
                onShareToWorkgroup={(p) => setShareProject(p)}
                onSelect={(p) => {
                    setAllProjectsOpen(false);
                    navigate(`/workspace/${p.id}`);
                }}
            />
            <SaveToWorkgroupDialog
                kind="project"
                itemId={shareProject?.id || null}
                itemName={shareProject?.name || ""}
                settingKey="share_projects"
                open={!!shareProject}
                onOpenChange={(o) => !o && setShareProject(null)}
                onSaved={() => setShareProject(null)}
            />
            <EditProjectDialog
                open={!!editingProject}
                onOpenChange={(o) => !o && setEditingProject(null)}
                project={editingProject}
                onSave={handleUpdateProject}
                onUploaded={fetchUploadCounts}
            />
        </div>
    );
}
